import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API from '../lib/api';
import { Users, TrendingUp, X, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';

function PlayerDetail({ player, onClose }) {
    if (!player) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="player-detail-modal">
            <div className="absolute inset-0 bg-black/80" onClick={onClose} />
            <div className="relative glass-card p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto" style={{ borderColor: `${player.team?.color}30` }}>
                <Button variant="ghost" size="icon" onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white">
                    <X className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-black" style={{ backgroundColor: `${player.team?.color}20`, color: player.team?.color }}>
                        {player.name?.charAt(0)}
                    </div>
                    <div>
                        <div className="heading-primary text-2xl text-white">{player.name}</div>
                        <div className="flex items-center gap-2">
                            <span className="tech-mono text-xs" style={{ color: player.team?.color }}>{player.team?.short_name}</span>
                            <span className="text-xs text-white/30">|</span>
                            <span className="tech-mono text-xs text-white/40">{player.role}</span>
                        </div>
                    </div>
                    <div className="ml-auto text-right">
                        <div className="tech-mono text-[10px] text-white/30">IMPACT SCORE</div>
                        <div className="heading-primary text-3xl neon-text">{player.impact_score}</div>
                    </div>
                </div>

                <div className="grid grid-cols-4 gap-3 mb-6">
                    <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="tech-mono text-[10px] text-white/30">RUNS</div>
                        <div className="heading-primary text-xl text-white">{player.runs}</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="tech-mono text-[10px] text-white/30">AVG</div>
                        <div className="heading-primary text-xl text-white">{player.batting_avg}</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="tech-mono text-[10px] text-white/30">SR</div>
                        <div className="heading-primary text-xl text-cyan-400">{player.strike_rate}</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="tech-mono text-[10px] text-white/30">WICKETS</div>
                        <div className="heading-primary text-xl text-pink-400">{player.wickets || 0}</div>
                    </div>
                </div>

                {player.recent_form && (
                    <div className="mb-6">
                        <h4 className="heading-primary text-sm text-white mb-3">RECENT FORM</h4>
                        <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={player.recent_form}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                                <XAxis dataKey="match" tick={{ fill: '#666', fontSize: 9 }} />
                                <YAxis tick={{ fill: '#666', fontSize: 10 }} />
                                <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} labelStyle={{ color: '#fff' }} />
                                <Bar dataKey="runs" fill="#00E5FF" opacity={0.7} radius={[4, 4, 0, 0]} name="Runs" />
                                {player.wickets > 0 && <Bar dataKey="wickets" fill="#FF2D95" opacity={0.7} radius={[4, 4, 0, 0]} name="Wickets" />}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {player.season_splits && (
                    <div>
                        <h4 className="heading-primary text-sm text-white mb-3">SEASON SPLITS</h4>
                        <div className="space-y-2">
                            {player.season_splits.map((s, i) => (
                                <div key={i} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                                    <span className="tech-mono text-xs text-white/60">{s.season}</span>
                                    <div className="flex items-center gap-4 text-sm">
                                        <span className="text-white">{s.runs} runs</span>
                                        <span className="text-cyan-400">Avg {s.avg}</span>
                                        <span className="text-yellow-400">SR {s.sr}</span>
                                        {s.wickets > 0 && <span className="text-pink-400">{s.wickets} wkts</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function PlayersPage() {
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [filter, setFilter] = useState('all');
    const [detailLoading, setDetailLoading] = useState(false);

    useEffect(() => {
        API.get('/players').then(res => {
            setPlayers(res.data.players || []);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const openPlayer = async (playerId) => {
        setDetailLoading(true);
        try {
            const res = await API.get(`/players/${playerId}`);
            setSelectedPlayer(res.data);
        } catch (err) {
            console.error(err);
        }
        setDetailLoading(false);
    };

    const filtered = filter === 'all' ? players : players.filter(p => {
        if (filter === 'batsman') return p.role === 'Batsman' || p.role === 'Wicketkeeper';
        if (filter === 'bowler') return p.role === 'Bowler';
        if (filter === 'allrounder') return p.role === 'All-rounder';
        return true;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="players-page">
            <div className="glass-card p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-purple-400" />
                        <h2 className="heading-primary text-base md:text-xl text-white">PLAYER INTELLIGENCE</h2>
                    </div>
                    <div className="flex gap-2">
                        {['all', 'batsman', 'bowler', 'allrounder'].map(f => (
                            <button key={f} data-testid={`filter-${f}`}
                                onClick={() => setFilter(f)}
                                className={`px-3 py-1.5 rounded text-xs font-bold uppercase transition-all ${filter === f ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-white/5 text-white/40 border border-white/5 hover:text-white'}`}>
                                {f === 'allrounder' ? 'All-rounder' : f}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filtered.map((player, i) => (
                    <div key={`${player.id}-${i}`} data-testid={`player-card-${player.id}`}
                        className={`glass-card p-5 cursor-pointer hover:border-purple-500/30 transition-all animate-fadeInUp stagger-${(i % 6) + 1}`}
                        onClick={() => openPlayer(player.id)}>
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <Link to={`/players/${player.id}`} className="text-sm font-bold text-white hover:text-cyan-400 transition-colors">{player.name}</Link>
                                <div className="tech-mono text-[10px] text-white/40">{player.team_id?.toUpperCase()} | {player.role}</div>
                            </div>
                            <div className="flex items-center gap-1">
                                <TrendingUp className="w-3 h-3 text-cyan-400" />
                                <span className="text-sm neon-text font-bold">{player.impact_score}</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-white/5 rounded p-2">
                                <div className="text-[10px] text-white/30">RUNS</div>
                                <div className="text-sm text-white font-bold">{player.runs}</div>
                            </div>
                            <div className="bg-white/5 rounded p-2">
                                <div className="text-[10px] text-white/30">SR</div>
                                <div className="text-sm text-cyan-400 font-bold">{player.strike_rate}</div>
                            </div>
                            <div className="bg-white/5 rounded p-2">
                                <div className="text-[10px] text-white/30">{player.wickets > 0 ? 'WKTS' : 'AVG'}</div>
                                <div className="text-sm text-pink-400 font-bold">{player.wickets > 0 ? player.wickets : player.batting_avg}</div>
                            </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                            <div className="text-[10px] text-white/30">Form:</div>
                            <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                <div className={`h-full rounded-full ${player.form > 0.85 ? 'bg-green-400' : player.form > 0.7 ? 'bg-yellow-400' : 'bg-red-400'}`}
                                    style={{ width: `${player.form * 100}%` }} />
                            </div>
                            <div className="text-[10px] text-white/40">{(player.form * 100).toFixed(0)}%</div>
                        </div>
                        {(player.impact_batting_shift !== null || player.impact_bowling_shift !== null) && (
                            <div className="mt-2 flex items-center gap-2 text-[10px]" data-testid={`impact-badge-${player.id}`}>
                                <Star className="w-3 h-3 text-orange-400" />
                                {player.impact_batting_shift !== null && (
                                    <span className="px-1.5 py-0.5 bg-green-500/10 text-green-400 rounded">BAT +{player.impact_batting_shift}%</span>
                                )}
                                {player.impact_bowling_shift !== null && (
                                    <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded">BOWL +{player.impact_bowling_shift}%</span>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {selectedPlayer && <PlayerDetail player={selectedPlayer} onClose={() => setSelectedPlayer(null)} />}
        </div>
    );
}
